import { useState } from "react";
import {
  ArrowRight,
  Banknote,
  BellRing,
  CheckCheck,
  ClipboardCheck,
  FilePlus2,
  FileText,
  Flag,
  Mail,
  MessageSquare,
  Paperclip,
  Pencil,
  Settings2,
  Workflow,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import { useOrderEvents } from "../hooks/useOrderEvents";
import type { OrderAuditEventKind } from "../types";

function fmtDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString("pl-PL", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

const KIND_META: Record<OrderAuditEventKind, { icon: typeof Workflow; cls: string }> = {
  created: { icon: FilePlus2, cls: "text-[var(--color-primary)]" },
  status: { icon: Workflow, cls: "text-[var(--color-primary)]" },
  acknowledgement: { icon: CheckCheck, cls: "text-green-600 dark:text-green-400" },
  milestone: { icon: Flag, cls: "text-gray-500 dark:text-gray-400" },
  attachment: { icon: Paperclip, cls: "text-gray-500 dark:text-gray-400" },
  result_file: { icon: FileText, cls: "text-gray-500 dark:text-gray-400" },
  comment: { icon: MessageSquare, cls: "text-gray-500 dark:text-gray-400" },
  change_request: { icon: Pencil, cls: "text-amber-600 dark:text-amber-400" },
  external_cost: { icon: Banknote, cls: "text-gray-500 dark:text-gray-400" },
  weekly_report: { icon: ClipboardCheck, cls: "text-gray-500 dark:text-gray-400" },
  penalty: { icon: BellRing, cls: "text-red-600 dark:text-red-400" },
  pock: { icon: ClipboardCheck, cls: "text-[var(--color-primary)]" },
  mail: { icon: Mail, cls: "text-gray-500 dark:text-gray-400" },
  edit: { icon: Pencil, cls: "text-amber-600 dark:text-amber-400" },
  settings: { icon: Settings2, cls: "text-gray-500 dark:text-gray-400" },
  other: { icon: Workflow, cls: "text-gray-500 dark:text-gray-400" },
};

const PAGE = 15;

interface Props {
  orderId: string;
  /** Internal roles only — the endpoint returns 403 for contractor accounts. */
  visible: boolean;
}

/**
 * Historia zdarzeń zamówienia — ujednolicony, chronologiczny zapis wszystkich akcji
 * koordynatora i podwykonawcy (statusy, kamienie, pliki, komentarze, wnioski, koszty,
 * raporty, POCK), łącznie ze zdarzeniami automatycznymi ("System").
 */
export function OrderEventsSection({ orderId, visible }: Props) {
  const { data: events, isLoading } = useOrderEvents(orderId, visible);
  const [expanded, setExpanded] = useState(false);

  if (!visible || isLoading || !events) return null;

  const shown = expanded ? events : events.slice(0, PAGE);

  return (
    <div className="mb-8">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3 border-l-4 border-[var(--color-primary)] pl-3">
        Historia zdarzeń ({events.length})
      </h2>
      {events.length === 0 && (
        <div className="bg-white dark:bg-gray-800 px-3 py-2.5 text-sm text-gray-500 dark:text-gray-400">
          Brak wpisów — historia zapisuje akcje koordynatora i podwykonawcy (statusy,
          pliki, komentarze, wnioski, odbiory) od momentu włączenia funkcji.
        </div>
      )}
      <div className="bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
        {shown.map((e) => {
          const meta = KIND_META[e.kind] ?? KIND_META.other;
          const Icon = meta.icon;
          return (
            <div key={e.id} className="px-3 py-2.5 flex gap-2.5">
              <Icon size={15} className={`mt-0.5 shrink-0 ${meta.cls}`} />
              <div className="min-w-0">
                <div className="text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap break-words">
                  {e.description}
                </div>
                {e.from_status && e.to_status && (
                  <div className="mt-0.5 inline-flex items-center gap-1 text-[11px] text-gray-500 dark:text-gray-400">
                    <span>{e.from_status_display}</span>
                    <ArrowRight size={11} />
                    <span className="font-medium">{e.to_status_display}</span>
                  </div>
                )}
                <div className="text-[11px] text-gray-400 dark:text-gray-500">
                  <span className="font-mono uppercase tracking-wide">{e.kind_display}</span>
                  {" · "}
                  {e.actor_name}
                  {" · "}
                  {fmtDateTime(e.created_at)}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {events.length > PAGE && (
        <Button size="sm" variant="ghost" onClick={() => setExpanded((v) => !v)} className="mt-2">
          {expanded ? "Pokaż mniej" : `Pokaż wszystkie (${events.length})`}
        </Button>
      )}
    </div>
  );
}
